/**
 * Stakeholder Engagement Island
 * A rich dimensional space for exploring stakeholder-related frameworks
 */

import type { DimensionalSpace, TerrainRegion, Path } from '../types/spatialKnowledge';

// Island dimensions: 1200 x 900 units
const ISLAND_WIDTH = 1200;
const ISLAND_HEIGHT = 900;

// TERRAIN REGIONS

const basicsBeach: TerrainRegion = {
  id: 'basics-beach',
  name: 'Foundation Beach',
  type: 'beach',
  description: 'Where every stakeholder journey begins. Accessible fundamentals and core concepts.',
  polygon: [
    { x: 100, y: 700 },
    { x: 500, y: 750 },
    { x: 450, y: 850 },
    { x: 50, y: 850 }
  ],
  elevation: 0,
  color: '#f4e4c1', // Sandy beach color
  landmarks: [
    {
      id: 'stakeholder-definition-marker',
      name: 'What is a Stakeholder?',
      type: 'marker',
      position: { x: 200, y: 780 },
      size: 'small',
      description: 'The foundational question that starts every stakeholder journey.',
      discovered: false,
      visited: false,
      icon: '🏖️'
    },
    {
      id: 'stakeholder-salience-monument',
      name: 'Stakeholder Salience Model',
      type: 'monument',
      position: { x: 350, y: 800 },
      size: 'large',
      frameworkId: 'stakeholder-salience',
      description: 'The landmark framework for understanding stakeholder importance through Power, Legitimacy, and Urgency.',
      insights: [
        'Not all stakeholders are equal',
        'Three dimensions reveal true priority',
        'Dynamic - salience changes over time'
      ],
      discovered: false,
      visited: false,
      icon: '🏛️',
      color: '#8b7355'
    }
  ],
  connectedTo: ['mapping-forest', 'communication-clearing'],
  paths: [],
  discovered: false,
  explorationLevel: 0
};

const mappingForest: TerrainRegion = {
  id: 'mapping-forest',
  name: 'Mapping Forest',
  type: 'forest',
  description: 'Navigate the complexity of stakeholder analysis. Multiple paths, hidden insights.',
  polygon: [
    { x: 400, y: 400 },
    { x: 700, y: 350 },
    { x: 750, y: 600 },
    { x: 500, y: 700 },
    { x: 350, y: 550 }
  ],
  elevation: 20,
  color: '#4a6741',
  landmarks: [
    {
      id: 'power-interest-grid-structure',
      name: 'Power-Interest Grid',
      type: 'structure',
      position: { x: 550, y: 500 },
      size: 'medium',
      frameworkId: 'power-interest-grid',
      description: 'The classic 2x2 matrix for stakeholder prioritization. A well-worn path in the forest.',
      discovered: false,
      visited: false,
      icon: '🗺️',
      color: '#6b8e65'
    },
    {
      id: 'influence-mapping-treasure',
      name: 'Hidden Influence Patterns',
      type: 'treasure',
      position: { x: 450, y: 450 },
      size: 'small',
      description: 'Discover the informal power structures that aren\'t on any org chart.',
      insights: [
        'Formal power ≠ actual influence',
        'Look for the connectors',
        'Map the informal network'
      ],
      discovered: false,
      visited: false,
      icon: '💎'
    },
    {
      id: 'ecosystem-viewpoint',
      name: 'Ecosystem Overview',
      type: 'viewpoint',
      position: { x: 650, y: 400 },
      size: 'medium',
      description: 'From here, you can see how all stakeholders connect in the broader ecosystem.',
      discovered: false,
      visited: false,
      icon: '🔭'
    }
  ],
  connectedTo: ['basics-beach', 'engagement-mountain', 'communication-clearing'],
  paths: [],
  discovered: false,
  explorationLevel: 0
};

const communicationClearing: TerrainRegion = {
  id: 'communication-clearing',
  name: 'Communication Clearing',
  type: 'clearing',
  description: 'A pause in the journey. Decide how to engage and communicate with stakeholders.',
  polygon: [
    { x: 200, y: 500 },
    { x: 400, y: 450 },
    { x: 380, y: 600 },
    { x: 180, y: 620 }
  ],
  elevation: 10,
  color: '#9db894',
  landmarks: [
    {
      id: 'communication-campsite',
      name: 'Engagement Strategy Camp',
      type: 'campsite',
      position: { x: 290, y: 530 },
      size: 'medium',
      description: 'Rest here and plan your communication approach. Consolidate what you\'ve learned.',
      discovered: false,
      visited: false,
      icon: '🏕️'
    },
    {
      id: 'messaging-framework-marker',
      name: 'Message Crafting Guide',
      type: 'marker',
      position: { x: 330, y: 560 },
      size: 'small',
      description: 'Tailor your message to each stakeholder group.',
      discovered: false,
      visited: false,
      icon: '💬'
    }
  ],
  connectedTo: ['basics-beach', 'mapping-forest', 'engagement-mountain'],
  paths: [],
  discovered: false,
  explorationLevel: 0
};

const engagementMountain: TerrainRegion = {
  id: 'engagement-mountain',
  name: 'Engagement Peak',
  type: 'mountain',
  description: 'The challenging ascent to mastering stakeholder engagement. Advanced concepts and integration.',
  polygon: [
    { x: 700, y: 150 },
    { x: 900, y: 200 },
    { x: 850, y: 400 },
    { x: 700, y: 350 }
  ],
  elevation: 60,
  color: '#7a8b99',
  landmarks: [
    {
      id: 'degap-monument',
      name: 'DEGAP Summit',
      type: 'monument',
      position: { x: 800, y: 250 },
      size: 'large',
      frameworkId: 'degap',
      description: 'At the peak, you find DEGAP - the ultimate framework for strategic stakeholder prioritization.',
      insights: [
        'Stakeholder engagement is strategic work',
        'Prioritization requires multiple lenses',
        'Integration of analysis → action'
      ],
      discovered: false,
      visited: false,
      icon: '⛰️',
      color: '#5c6b7a'
    },
    {
      id: 'coalition-building-structure',
      name: 'Coalition Bridge',
      type: 'structure',
      position: { x: 750, y: 300 },
      size: 'medium',
      description: 'Learn to build alliances across stakeholder groups.',
      discovered: false,
      visited: false,
      icon: '🌉'
    }
  ],
  connectedTo: ['mapping-forest', 'communication-clearing', 'resistance-cave'],
  paths: [],
  discovered: false,
  explorationLevel: 0
};

const resistanceCave: TerrainRegion = {
  id: 'resistance-cave',
  name: 'Resistance Caverns',
  type: 'cave',
  description: 'Hidden depths where you confront opposition and learn to navigate resistance.',
  polygon: [
    { x: 900, y: 350 },
    { x: 1050, y: 380 },
    { x: 1000, y: 500 },
    { x: 850, y: 450 }
  ],
  elevation: -20, // Below surface
  color: '#3a4a5a',
  landmarks: [
    {
      id: 'resistance-patterns-treasure',
      name: 'Sources of Resistance',
      type: 'treasure',
      position: { x: 950, y: 420 },
      size: 'medium',
      description: 'Deep insight into why stakeholders resist and how to address it.',
      insights: [
        'Resistance is data, not defiance',
        'Understand the fear behind the "no"',
        'Change equation: Dissatisfaction × Vision × First Steps > Cost of Change'
      ],
      discovered: false,
      visited: false,
      icon: '🔦'
    }
  ],
  connectedTo: ['engagement-mountain'],
  paths: [],
  discovered: false,
  explorationLevel: 0
};

// PATHS connecting regions

const beachToForest: Path = {
  id: 'foundation-to-analysis',
  name: 'Trail of Understanding',
  from: 'basics-beach',
  to: 'mapping-forest',
  waypoints: [
    { x: 450, y: 750 },
    { x: 420, y: 650 },
    { x: 400, y: 550 }
  ],
  difficulty: 2,
  length: 200,
  type: 'trail',
  discovered: false,
  traveled: false
};

const beachToClearing: Path = {
  id: 'direct-communication',
  name: 'Direct Path',
  from: 'basics-beach',
  to: 'communication-clearing',
  waypoints: [
    { x: 300, y: 750 },
    { x: 250, y: 650 }
  ],
  difficulty: 1,
  length: 150,
  type: 'road',
  discovered: false,
  traveled: false
};

const forestToMountain: Path = {
  id: 'analysis-to-engagement',
  name: 'Ascent Trail',
  from: 'mapping-forest',
  to: 'engagement-mountain',
  waypoints: [
    { x: 700, y: 400 },
    { x: 720, y: 320 },
    { x: 750, y: 280 }
  ],
  difficulty: 4,
  length: 180,
  type: 'trail',
  discovered: false,
  traveled: false
};

const mountainToCave: Path = {
  id: 'confronting-resistance',
  name: 'Hidden Passage',
  from: 'engagement-mountain',
  to: 'resistance-cave',
  waypoints: [
    { x: 850, y: 350 },
    { x: 900, y: 380 }
  ],
  difficulty: 5,
  length: 100,
  type: 'tunnel',
  discovered: false,
  traveled: false
};

// THE COMPLETE ISLAND

export const stakeholderEngagementIsland: DimensionalSpace = {
  id: 'stakeholder-engagement-island',
  name: 'Stakeholder Engagement Island',
  type: 'island',
  description: 'Explore the rich landscape of stakeholder engagement frameworks. From foundational concepts on the beach to advanced strategies at the mountain peak.',
  
  bounds: {
    minX: 0,
    maxX: ISLAND_WIDTH,
    minY: 0,
    maxY: ISLAND_HEIGHT
  },
  
  atmosphereColor: '#87CEEB', // Sky blue
  
  regions: [
    basicsBeach,
    mappingForest,
    communicationClearing,
    engagementMountain,
    resistanceCave
  ],
  
  globalPaths: [
    beachToForest,
    beachToClearing,
    forestToMountain,
    mountainToCave
  ],
  
  entryPoints: [
    { x: 200, y: 800 } // Start at the beach
  ],
  
  visitedRegions: [],
  discoveredLandmarks: [],
  
  learningObjectives: [
    'Understand who stakeholders are and why they matter',
    'Learn to map and analyze stakeholder landscapes',
    'Develop engagement and communication strategies',
    'Master advanced prioritization and coalition building',
    'Navigate resistance and opposition effectively'
  ],
  
  recommendedFor: [
    'Project managers starting stakeholder work',
    'Strategy consultants',
    'Change leaders',
    'Anyone managing complex relationships'
  ],
  
  estimatedExplorationTime: 45 // minutes
};

